import { DocumentViewer } from '@aecom-ai-engineering/react-components';
import { Checkbox, DEFAULT_PAGE_SIZE, LoadingArea, Pagination, Position } from '@consigli/facade';
import {
  useProjectId,
  usePackageId,
  useProject,
  useDebouncedSearch,
  useBlobs,
  useDownloadFilesAsZip,
} from '@consigli/hooks';
import { BlobParsedStatus, FoldersDocumentType } from '@consigli/types';
import { UniqueIdentifier } from '@dnd-kit/core';
import { Dispatch, SetStateAction, useState, useMemo, useCallback, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { FaRegFolder } from 'react-icons/fa';

import { MultipleSelectionDialog } from '@/components/multiple-selection-dialog';
import { ServiceHeader } from '@/components/service-header';
import { DownloadSelected } from '@/features/document-list/download-selected';
import { createRenderers } from '@/features/document-viewer/renderers';
import { useFileTreeContext } from '@/pages/insight/folders/use-file-tree-context';
import { TreeFolder, createTreeFile } from '@/pages/insight/folders/util/document-list-to-tree';
import { convertFolderDocumentsTypeToDocumentsType } from '@/util/convert-folder-documents-type-to-documents-type';
import { useDemoMode } from '@/util/demo-mode';
import { resolveDocumentFileType, resolveDocumentSrc } from '@/util/document-check';
import { FolderDocumentsType } from '@/util/types';

import { CreatePortal } from './create-portal';
import { DocumentFilters } from './document-filters';
import { EditOperationsFilesButton } from './edit-files-button';
import { FoldersCardList } from './folders-cardlist';

interface DraggableContainerProps {
  activeFolder: TreeFolder | null;
  draggingCardId: UniqueIdentifier;
  selectedFiles: FolderDocumentsType[] | [];
  setSelectedFiles: Dispatch<SetStateAction<FolderDocumentsType[] | []>>;
}
export const status: BlobParsedStatus[] = [
  BlobParsedStatus.PARSED,
  BlobParsedStatus.UNPARSED,
  BlobParsedStatus.PARSING_FAILED,
  BlobParsedStatus.UPLOADED,
];
export const DraggableContainer = ({
  activeFolder,
  draggingCardId,
  selectedFiles,
  setSelectedFiles,
}: DraggableContainerProps) => {
  const { t } = useTranslation();

  const projectId = useProjectId();
  const packageId = usePackageId();
  const { project } = useProject(projectId);

  const [selectAllChecked, setSelectAllChecked] = useState(false);
  const [showDownloadPopup, setShowDownloadPopup] = useState(false);
  const [activeDocumentId, setActiveDocumentId] = useState<string | null>(null);
  const { adminFeaturesEnabled } = useDemoMode();

  const {
    activeFolderFiles,
    summaryCount,
    activeFolderId,
    page,
    setPage,
    setActiveFolderFiles,
    selectedDocumentTypes,
    searchContent,
    search,
    labelsReused,
    setLabelsReused,
  } = useFileTreeContext();
  const debouncedSearchContent = useDebouncedSearch(searchContent, 500);
  const debouncedSearch = useDebouncedSearch(search, 500);

  const { blobs, paginatedCount, isFetching } = useBlobs({
    projectId,
    packageId,
    page,
    search: debouncedSearch,
    searchParsedData: debouncedSearchContent.length >= 2 ? debouncedSearchContent : '',
    documentType: selectedDocumentTypes.map((option) => option.value as FoldersDocumentType),
    standard: activeFolderId,
    status,
    labelsReused,
  });

  const effectiveBlobs = useMemo(() => {
    return blobs;
  }, [blobs]);

  const entries = useMemo(
    () => activeFolderFiles.map((file) => createTreeFile(file)),
    [activeFolderFiles],
  );

  const files = useMemo(
    () =>
      entries?.map((x) => {
        return { ...x.blob, checked: false };
      }),
    [entries],
  );

  const [pageRecords, setPageRecords] = useState<FolderDocumentsType[]>(files);

  const activeDocumentIndex = pageRecords.findIndex((record) => record.id === activeDocumentId);
  const activeDocument = activeDocumentIndex >= 0 ? pageRecords[activeDocumentIndex] : null;
  const renderers = useMemo(() => createRenderers(), []);

  const draggingfile = pageRecords.find((x) => x.id === draggingCardId);

  const mappedSelectedFiles = useMemo(() => {
    return selectedFiles.map((blob: FolderDocumentsType) =>
      convertFolderDocumentsTypeToDocumentsType(blob),
    );
  }, [selectedFiles]);

  const handleSelectAllCheckboxClick = () => {
    setSelectAllChecked(!selectAllChecked);
    setPageRecords(
      pageRecords.map(
        (item: FolderDocumentsType) => item && { ...item, checked: !selectAllChecked },
      ),
    );
  };

  const {
    downloadFileZip: downloadSelected,
    isDownloading: isDownloadingSelected,
    abortDownload,
  } = useDownloadFilesAsZip(project?.name ?? 'files');

  const resetSelectAll = useCallback(() => {
    setSelectAllChecked(false);
    setPageRecords((prevData: FolderDocumentsType[]) =>
      prevData.map((item: FolderDocumentsType) => ({ ...item, checked: false })),
    );
    setShowDownloadPopup(false);
  }, []);

  const handleCheckboxClick = (record: FolderDocumentsType) => {
    setPageRecords((prevData: FolderDocumentsType[]) =>
      prevData?.map((item: FolderDocumentsType) =>
        item.id === record.id ? { ...item, checked: !item.checked } : item,
      ),
    );
  };

  const handleDocumentSelect = useCallback((file: FolderDocumentsType) => {
    setActiveDocumentId(file.id);
  }, []);

  const onClickDownloadSelected = async () => {
    await downloadSelected(mappedSelectedFiles);
    resetSelectAll();
    setShowDownloadPopup(false);
  };

  const handlePageChange = useCallback(
    (page: number) => {
      setPage(page);
    },
    [setPage],
  );

  useEffect(() => {
    setActiveFolderFiles(effectiveBlobs);
  }, [effectiveBlobs, setActiveFolderFiles]);

  useEffect(() => {
    setSelectedFiles(pageRecords?.filter((file: FolderDocumentsType) => file.checked));
    if (selectedFiles?.length > 1) setShowDownloadPopup(true);
  }, [selectedFiles?.length, pageRecords, setSelectedFiles]);

  useEffect(() => {
    setPageRecords(files);
  }, [files, activeFolder?.children, t]);

  useEffect(() => {
    const checkedCount =
      pageRecords?.filter((file: FolderDocumentsType) => file.checked).length || 0;

    if (checkedCount === 0) {
      setSelectAllChecked(false);
    } else if (checkedCount === pageRecords.length) {
      setSelectAllChecked(true);
    } else if (selectAllChecked && checkedCount < pageRecords.length) {
      setSelectAllChecked(false);
    }
  }, [pageRecords, selectAllChecked]);

  const filterItems = useCallback(
    (items: FolderDocumentsType[]) => {
      const filterValues = selectedFiles.map((item: FolderDocumentsType) => item.id);
      if (!draggingCardId || !filterValues.includes(String(draggingCardId))) {
        return items;
      }
      return items.filter((item) => item.id === draggingCardId || !filterValues.includes(item.id));
    },
    [draggingCardId, selectedFiles],
  );

  const handleClosePopup = useCallback(() => {
    resetSelectAll();
    abortDownload();
  }, [resetSelectAll, abortDownload]);

  const handleHideLabelsReused = () => {
    setLabelsReused(!labelsReused);
  };

  return (
    <>
      <div className="p-0 lg:px-8 flex-1 bg-day-light-3">
        <div className="px-4 pt-6 relative">
          <div className="flex items-center justify-between">
            <ServiceHeader
              icon={FaRegFolder}
              count={(summaryCount && summaryCount[activeFolderId]) || paginatedCount}
              message={t('document-list.documents')}
              title={activeFolder?.toDisplayString() || t('folders.all-documents')}
            />
            {adminFeaturesEnabled && (
              <Checkbox
                checked={labelsReused}
                label="Hide reused labels"
                value="Hide reused labels"
                id="HideLabelsReused"
                labelPosition={Position.RIGHT}
                onChange={handleHideLabelsReused}
              />
            )}
          </div>
          <div className="mr-4 pt-4 pb-2 flex w-full">
            <Checkbox
              checked={selectAllChecked}
              label={t('findings.select-all')}
              value="Select all"
              id="foldersSelectAll"
              labelPosition={Position.RIGHT}
              onChange={handleSelectAllCheckboxClick}
            />
            <DocumentFilters />
          </div>
        </div>
        {isFetching ? (
          <LoadingArea />
        ) : (
          <FoldersCardList
            filterItems={filterItems}
            handleCheckboxClick={handleCheckboxClick}
            pageRecords={pageRecords}
            onDocumentSelect={handleDocumentSelect}
          />
        )}
        <div className="py-2 flex flex-col md:flex-row justify-center items-center gap-8 text-day-neutral-dark">
          <div className="text-sm font-semibold">
            {t('findings.showing_findings', { count: files.length || 0 })}
          </div>
          <Pagination
            currentPage={page}
            totalCount={paginatedCount}
            pageSize={DEFAULT_PAGE_SIZE}
            onPageChange={handlePageChange}
          />
        </div>
        <MultipleSelectionDialog
          dragId="foldersDraggable"
          title={t('document-list.selected-documents', { count: files.length })}
          onClose={handleClosePopup}
        >
          {showDownloadPopup && selectedFiles?.length > 1 && (
            <div className="flex gap-2">
              <EditOperationsFilesButton
                documents={selectedFiles}
                setShowDownloadPopup={setShowDownloadPopup}
              />
              <DownloadSelected
                onClickDownloadSelected={onClickDownloadSelected}
                isLoading={isDownloadingSelected}
              />
            </div>
          )}
        </MultipleSelectionDialog>
        <CreatePortal
          draggingCardId={draggingCardId}
          draggingfile={draggingfile}
          handleCheckboxClick={handleCheckboxClick}
          selectedFiles={selectedFiles}
        />
      </div>
      {activeDocument && (
        <DocumentViewer
          open={activeDocumentIndex >= 0}
          onOpenChange={(details) => {
            if (!details.open) setActiveDocumentId(null);
          }}
          src={resolveDocumentSrc(activeDocument)}
          fileType={resolveDocumentFileType(activeDocument)}
          fileName={activeDocument.name}
          title={activeDocument.name}
          showSidebar={false}
          showNavigationActions={pageRecords.length > 1}
          previousDisabled={activeDocumentIndex <= 0}
          nextDisabled={activeDocumentIndex >= pageRecords.length - 1}
          onPrevious={() => setActiveDocumentId(pageRecords[activeDocumentIndex - 1]?.id ?? null)}
          onNext={() => setActiveDocumentId(pageRecords[activeDocumentIndex + 1]?.id ?? null)}
          renderers={renderers}
        />
      )}
    </>
  );
};
